import { useState, useEffect } from 'react';
import { User } from '@/types';
import { authApi } from '@/services/api';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await authApi.getMe();
          if (response.success && response.data) {
            setUser(response.data.user);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);

      if (response.success && response.data) {
        // CR-17: 2FA-enabled account — caller must complete the second step
        if (response.data.requires2FA) {
          return { success: true, requires2FA: true, tempToken: response.data.tempToken as string };
        }
        const { user: userData, token } = response.data;
        setUser(userData);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  // CR-17: finish a login that was paused for two-factor auth
  const complete2FALogin = async (tempToken: string, code: string) => {
    try {
      const response = await authApi.verify2FALogin(tempToken, code);
      if (response.success && response.data) {
        const { user: userData, token } = response.data;
        setUser(userData);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true };
      }
      return { success: false, error: response.error || 'Verification failed' };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Verification failed' };
    }
  };

  // CR-17: re-sync the current user from the server (e.g. after a forced password reset clears the flag)
  const refreshUser = async () => {
    try {
      const response = await authApi.getMe();
      if (response.success && response.data) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  return {
    user,
    isLoading,
    login,
    complete2FALogin,
    logout,
    refreshUser,
  };
};